import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import {
  ButtonComponent,
  CardComponent,
  CheckboxComponent,
  EmailInputComponent,
  FormGroupComponent,
  IconComponent,
  LinkComponent,
  RowComponent,
  TextComponent,
  ValidationFeedbackComponent,
} from 'core-ui';

import { PistonLoaderComponent } from '../../../components/piston-loader/piston-loader.component';
import { APPOINTMENT_ROUTES, AppointmentRouteKey } from '../../../consts';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { BookingDataService } from '../../../services/booking-data.service';
import { BookingSubmitService } from '../../../services/booking-submit.service';
import {
  EntryPrefillService,
  START_EMAIL_VALIDATORS,
} from '../../../services/entry-prefill.service';
import { dealerConfig } from '../../../state/dealer.config';
import { SessionService } from '../authenticate/services/session.service';
import { PlanAppointmentQueryParams } from '../plan-appointment/license-plate-selection/interfaces/plan-appointment-query-params.interface';

@Component({
  selector: 'app-home',
  imports: [
    CardComponent,
    RowComponent,
    TextComponent,
    ReactiveFormsModule,
    FormGroupComponent,
    EmailInputComponent,
    CheckboxComponent,
    ValidationFeedbackComponent,
    ButtonComponent,
    IconComponent,
    LinkComponent,
    PistonLoaderComponent,
    TranslatePipe,
  ],
  templateUrl: './start.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [':host { display: block; }'],
})
export class StartPage {
  private readonly formBuilder: FormBuilder = new FormBuilder();
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly sessionService: SessionService = inject(SessionService);
  private readonly bookingSubmit: BookingSubmitService = inject(BookingSubmitService);
  private readonly bookingData: BookingDataService = inject(BookingDataService);
  private readonly entryPrefill: EntryPrefillService = inject(EntryPrefillService);

  // Dealer config (terms / privacy links + theming) is global state, populated
  // by the dealer-config fetch in App. Read the signal directly — the template
  // reacts to it as soon as the fetch fills it. (The old toObservable()/toSignal()
  // round-trip broke: toObservable() ran outside an injection context inside the
  // async logout() switchMap, throwing NG0203 and leaving config() undefined.)
  protected readonly config = dealerConfig;

  // While the session probe is in flight we show a loader instead of the email
  // form, so a returning user with a live session never sees the form flash
  // before we redirect them past start.
  protected readonly checking = signal<boolean>(true);

  protected submitted: boolean = false;
  protected readonly startForm: FormGroup = this.formBuilder.group({
    email: [null, START_EMAIL_VALIDATORS],
    agree: [false, Validators.requiredTrue],
  });

  public constructor() {
    // The e-mail in sessionStorage is the trigger. With one we ask the backend
    // whether its session is still valid: valid → skip start and redirect;
    // invalid → wipe the stale local data and show the form. Without an e-mail
    // there is nothing to resume (sessionStorage is per-tab), so we go straight
    // to the form. We never call the backend logout: an invalid session is
    // already gone server-side, and clearSession() handles the local wipe.
    // Aankomst op start = nieuwe journey: een eerder afgeronde boeking blokkeert de wizard
    // niet meer (zie bookingSubmittedGuard) en niets van een vorige boeking (mileage,
    // form-caches, staps-selecties) mag de verse formulieren nog prefillen.
    this.bookingSubmit.submitted.set(false);
    this.sessionService.clearBookingData();
    this.bookingData.clear();

    // Deeplink prefill (SW-427.1): only fills the field; consent + submit stay with the user.
    const prefillEmail: string | null = this.entryPrefill.consumeEmail();
    if (prefillEmail !== null) {
      this.startForm.patchValue({ email: prefillEmail });
    }

    const email: string | null = this.sessionService.getAuthenticatedEmail();

    if (email === null) {
      this.sessionService.clearSession();
      this.checking.set(false);

      return;
    }

    this.sessionService
      .checkSession(email)
      .pipe(takeUntilDestroyed())
      .subscribe((hasValidSession: boolean): void => {
        if (hasValidSession) {
          this.navigateToTarget();

          return;
        }

        this.sessionService.clearSession();
        this.checking.set(false);
      });
  }

  protected onSubmit(): void {
    if (!this.startForm.valid) {
      this.submitted = true;
      return;
    }

    const queryParams: PlanAppointmentQueryParams = {
      authenticationMail: this.startForm.value.email,
    };

    this.router.navigate([APPOINTMENT_ROUTES.AUTHENTICATE_URL], { queryParams });
  }

  // Sends a returning user past start. Target = the requested ?redirect step
  // (validated against the known routes) or the license-plate selection screen
  // as fallback. We forward the context params the next steps rely on
  // (manufacturer for theming, dealerId) and drop the now-consumed redirect
  // param. We deliberately do NOT carry licensePlate: every redirect target here
  // is a pre-selection step, so a plate in the URL would be a stale leak (the
  // stripLicensePlateGuard also enforces this).
  private navigateToTarget(): void {
    const params: ParamMap = this.route.snapshot.queryParamMap;
    const redirect: string | null = params.get('redirect');
    const targetKey: AppointmentRouteKey =
      redirect !== null && redirect in APPOINTMENT_ROUTES
        ? (redirect as AppointmentRouteKey)
        : 'LICENSE_PLATE_SELECTION';

    const carried: Params = {};
    for (const key of ['dealerId', 'manufacturer'] as const) {
      const value: string | null = params.get(key);
      if (value !== null) {
        carried[key] = value;
      }
    }

    this.router.navigate([APPOINTMENT_ROUTES[targetKey]], { queryParams: carried });
  }
}
